/*
 * Iter 31.328 (05 Aug 2026) — shared audio visualiser.
 *
 * Signature Web-Audio spectrum bars that pulse to the mix, extracted
 * from RadioPlayer.jsx so every station surface (internal /radio/:slug,
 * /player/:slug, plus the public spyradio.uk pages) gets the same
 * visual identity.
 *
 * Contract
 * --------
 *   <AudioVisualiser
 *     audioRef={audioRef}     // ref to the <audio> element being played
 *     playing={playing}       // boolean; when false the draw loop idles
 *     palette={{ accent, accentDim }}  // optional; defaults to steel-blue
 *     height={80}             // canvas CSS height in px (default 80)
 *     bars={64}               // frequency bar count (default 64)
 *   />
 *
 * Contract notes
 * --------------
 * - The `<audio>` element MUST have `crossOrigin="anonymous"` when
 *   streaming from cross-origin sources (e.g. R2 CDN at
 *   tracks.spyradio.uk) so `createMediaElementSource` doesn't taint the
 *   audio graph. Set that on the caller's <audio>.
 * - The AudioContext is lazily created on first `playing=true` because
 *   Chrome's autoplay policy forbids AC creation before a user gesture.
 * - Only ONE `createMediaElementSource` is ever created per <audio>
 *   node in a browser session — subsequent calls throw. We guard with
 *   a ref so remounts don't crash.
 */
import React, { useEffect, useRef } from "react";

const DEFAULT_PALETTE = {
    accent: "#4A9EBF",
    accentDim: "#3A7A94",
};

export default function AudioVisualiser({
    audioRef,
    playing,
    palette = DEFAULT_PALETTE,
    height = 80,
    bars = 64,
    testId = "audio-visualiser",
}) {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);
    const rafRef = useRef(null);

    // Lazy Web-Audio graph creation on first play.
    useEffect(() => {
        if (!playing) return;
        if (ctxRef.current || !audioRef?.current) return;

        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;

        try {
            const ctx = new AC();
            // If the <audio> already has a source node from a prior mount
            // (browser retains it), this will throw. Swallow — bars just
            // won't animate; audio still plays.
            const source = ctx.createMediaElementSource(audioRef.current);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.82;
            source.connect(analyser);
            analyser.connect(ctx.destination);
            ctxRef.current = ctx;
            analyserRef.current = analyser;
            sourceRef.current = source;
        } catch (err) {
            // Common: MediaElementAudioSource already exists on this <audio>.
            // Non-fatal — audio still plays, just no visualiser.
            // eslint-disable-next-line no-console
            console.warn("[AudioVisualiser] analyser setup skipped:", err.message);
        }
    }, [playing, audioRef]);

    // HiDPI canvas sizing.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
        };
        resize();
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);

    // Spectrum draw loop — idle when paused so no CPU burn.
    useEffect(() => {
        const draw = () => {
            const canvas = canvasRef.current;
            const analyser = analyserRef.current;
            if (!canvas || !analyser) {
                rafRef.current = requestAnimationFrame(draw);
                return;
            }
            const w = canvas.width;
            const h = canvas.height;
            const g = canvas.getContext("2d");
            g.clearRect(0, 0, w, h);
            const bins = analyser.frequencyBinCount;
            const data = new Uint8Array(bins);
            analyser.getByteFrequencyData(data);
            const barW = w / bars;
            for (let i = 0; i < bars; i++) {
                const t = i / bars;
                // Log-ish frequency mapping — high end gets more real estate
                // than the FFT's linear allocation would give.
                const idx = Math.floor(Math.pow(t, 1.6) * bins);
                const v = data[idx] / 255;
                const barH = Math.max(2, v * h);
                const y = h - barH;
                const grad = g.createLinearGradient(0, h, 0, y);
                grad.addColorStop(0, palette.accentDim);
                grad.addColorStop(1, palette.accent);
                g.fillStyle = grad;
                g.fillRect(i * barW + 1, y, barW - 2, barH);
            }
            rafRef.current = requestAnimationFrame(draw);
        };
        if (playing) {
            rafRef.current = requestAnimationFrame(draw);
        }
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
    }, [playing, bars, palette.accent, palette.accentDim]);

    return (
        <canvas
            ref={canvasRef}
            data-testid={testId}
            style={{
                width: "100%",
                height: `${height}px`,
                display: "block",
            }}
        />
    );
}
